import { useEffect, useState } from "react";

import useWarehouseLocations from "../../data/useWarehouseLocations";
import Modal from "../../layouts/Modal";
import {
  IWarehouseLocationImportOutcome,
  IWarehouseLocationImportResult,
} from "../../models/Location";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

interface WarehouseLocationsImportModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Long lists get checked, not read - listing every one of several hundred skipped names would bury
// the summary that matters.
const maxDetailRows = 100;

export default function WarehouseLocationsImportModal({
  open,
  setOpen,
}: WarehouseLocationsImportModalProps) {
  const { importLocations } = useWarehouseLocations();
  const { addNotification } = useNotification();

  const [names, setNames] = useState("");
  const [capacity, setCapacity] = useState(1);
  const [preview, setPreview] = useState<IWarehouseLocationImportResult | undefined>(undefined);
  const [created, setCreated] = useState<IWarehouseLocationImportResult | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;

    setNames("");
    setCapacity(1);
    setPreview(undefined);
    setCreated(undefined);
    setError(undefined);
  }, [open]);

  // Editing the list throws the preview away. Without this an admin could check one list and then
  // commit a different one, which is the mistake the preview exists to prevent.
  const handleNamesChange = (value: string) => {
    setNames(value);
    setPreview(undefined);
    setError(undefined);
  };

  const handlePreview = () => {
    setError(undefined);

    importLocations.mutate(
      { names, capacity, previewOnly: true },
      {
        onSuccess: (response) => setPreview(response.data),
        onError: (err: any) => setError(err?.message ?? "Could not check that list"),
      }
    );
  };

  const handleImport = () => {
    setError(undefined);

    importLocations.mutate(
      { names, capacity, previewOnly: false },
      {
        onSuccess: (response) => {
          setCreated(response.data);
          addNotification({
            variant: "success",
            primaryText: "Created " + response.data.toCreate.length + " warehouse locations",
          });
        },
        onError: (err: any) => setError(err?.message ?? "Could not import those locations"),
      }
    );
  };

  const summary = created ?? preview;

  return (
    <Modal width="max-w-3xl" open={open} setOpen={setOpen} >
      <div className="text-left">
        <h2 className="text-lg font-medium text-gray-900">Import locations</h2>
        <p className="mt-1 text-sm text-gray-500">
          Paste your location names, one per line. Names that already exist are skipped, so it is
          safe to paste a list you have partly added before.
        </p>

        {created === undefined && (
          <>
            <div className="mt-4">
              <label htmlFor="import-names" className="block text-sm font-medium text-gray-700">
                Location names
              </label>
              <textarea
                id="import-names"
                rows={10}
                value={names}
                onChange={(event) => handleNamesChange(event.target.value)}
                placeholder={"L1A-1-0\nL1A-1-1\nL1A-1-2"}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div className="mt-4 w-full sm:max-w-[10rem]">
              <label htmlFor="import-capacity" className="block text-sm font-medium text-gray-700">
                Capacity each
              </label>
              <input
                id="import-capacity"
                type="number"
                min={0}
                value={capacity}
                onChange={(event) => setCapacity(Number(event.target.value))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {capacity === 0 && (
                <p className="mt-1 text-xs text-red-600">
                  A capacity of 0 shows every location as full.
                </p>
              )}
            </div>
          </>
        )}

        {error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {summary && (
          <div className="mt-4 rounded-md border border-gray-200 p-3">
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label={created ? "Created" : "To create"} value={summary.toCreate.length} />
              <Stat label="Already set up" value={summary.alreadyExists.length} />
              <Stat label="Repeated in list" value={summary.repeatedInPaste.length} />
              <Stat label="Not usable" value={summary.invalid.length} />
            </dl>

            {created === undefined && preview !== undefined && preview.toCreate.length > 0 && (
              <p className="mt-3 text-sm text-gray-500">
                Each one will be created with a capacity of {capacity}.
              </p>
            )}

            {created === undefined && preview !== undefined && preview.toCreate.length === 0 && (
              <p className="mt-3 text-sm text-gray-500">
                There is nothing new in this list - every name is already set up.
              </p>
            )}

            <OutcomeList title="Already set up" outcomes={summary.alreadyExists} />
            <OutcomeList title="Repeated in list" outcomes={summary.repeatedInPaste} />
            <OutcomeList title="Not usable" outcomes={summary.invalid} />
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          {created === undefined && (
            <>
              <ButtonInput
                label="Cancel"
                isSubmit={false}
                onClick={() => setOpen(false)}
                classes="bg-gray-200 hover:bg-gray-300 text-gray-800"
                disabled={importLocations.isLoading}
              />
              {preview === undefined ? (
                <ButtonInput
                  label="Check list"
                  isSubmit={false}
                  onClick={handlePreview}
                  classes={undefined}
                  disabled={names.trim().length === 0}
                  isLoading={importLocations.isLoading}
                />
              ) : (
                <ButtonInput
                  label={"Create " + preview.toCreate.length + " locations"}
                  isSubmit={false}
                  onClick={handleImport}
                  classes={undefined}
                  disabled={preview.toCreate.length === 0}
                  isLoading={importLocations.isLoading}
                />
              )}
            </>
          )}

          {created !== undefined && (
            <ButtonInput
              label="Done"
              isSubmit={false}
              onClick={() => setOpen(false)}
              classes={undefined}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-gray-50 px-3 py-2">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-lg font-medium text-gray-900">{value}</dd>
    </div>
  );
}

function OutcomeList({
  title,
  outcomes,
}: {
  title: string;
  outcomes: IWarehouseLocationImportOutcome[];
}) {
  if (outcomes.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {title} ({outcomes.length})
      </p>
      <div className="mt-1 max-h-32 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2">
        <p className="font-mono text-xs text-gray-700">
          {outcomes
            .slice(0, maxDetailRows)
            .map((outcome) => outcome.name)
            .join(", ")}
          {outcomes.length > maxDetailRows && " and " + (outcomes.length - maxDetailRows) + " more"}
        </p>
      </div>
    </div>
  );
}
